import React, { useMemo, useState } from 'react'
import axios from 'axios'

import {
  Link,
  useNavigate
} from 'react-router-dom'

import { useSelector } from 'react-redux'

import logo from '../../logo.png'

import {
  FiArrowRight,
  FiCheck,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiShield,
  FiUserCheck
} from 'react-icons/fi'


export default function Signup() {

  const navigate = useNavigate()

  const url =
    useSelector(state => state.url)


  const [email, setEmail] =
    useState('')

  const [bvn, setBvn] =
    useState('')

  const [password, setPassword] =
    useState('')

  const [showPassword, setShowPassword] =
    useState(false)

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')

  const [success, setSuccess] =
    useState(false)


  const passwordChecks =
    useMemo(() => {

      return {
        length:
          password.length >= 8,

        uppercase:
          /[A-Z]/.test(password),

        lowercase:
          /[a-z]/.test(password),

        number:
          /[0-9]/.test(password),

        symbol:
          /[^A-Za-z0-9]/.test(password)
      }

    }, [password])


  const passwordValid =
    Object.values(
      passwordChecks
    ).every(Boolean)


  const signUp = async (event) => {

    event.preventDefault()

    setError('')


    const cleanEmail =
      email.trim().toLowerCase()

    const cleanBvn =
      bvn.trim()


    if (!cleanEmail) {

      setError(
        'Please enter your email address.'
      )

      return
    }


    const emailPattern =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/


    if (
      !emailPattern.test(cleanEmail)
    ) {

      setError(
        'Please enter a valid email address.'
      )

      return
    }


    if (!cleanBvn) {

      setError(
        'Please enter your BVN.'
      )

      return
    }


    if (!/^\d{11}$/.test(cleanBvn)) {

      setError(
        'Your BVN must contain exactly 11 digits.'
      )

      return
    }


    if (!password) {

      setError(
        'Please create a password.'
      )

      return
    }


    if (!passwordValid) {

      setError(
        'Your password does not meet all the requirements.'
      )

      return
    }


    try {

      setLoading(true)


      const formData =
        new FormData()


      formData.append(
        'email',
        cleanEmail
      )

      formData.append(
        'bvn',
        cleanBvn
      )

      formData.append(
        'password',
        password
      )


      const response =
        await axios.post(
          `${url}/auth/register`,
          formData
        )


      const data =
        response.data


      if (data.status === 200) {

        setSuccess(true)


        setTimeout(() => {

          navigate(
            '/auth/signin/'
          )

        }, 2500)

        return
      }


      setError(
        data.error ||
        'We could not create your account.'
      )


    } catch (err) {

      console.error(
        'Registration error:',
        err
      )


      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Unable to create your account. Please try again.'
      )

    } finally {

      setLoading(false)
    }
  }


  return (

    <div className="
      min-h-screen
      bg-[#F7F9FC]
      font-[Satoshi]
      grid
      grid-cols-1
      lg:grid-cols-2
    ">


      {/* LEFT */}

      <section className="
        hidden
        lg:flex
        relative
        overflow-hidden
        bg-[#101828]
        text-white
        p-12
        xl:p-16
        flex-col
        justify-between
        min-h-screen
      ">


        <div className="
          absolute
          w-[500px]
          h-[500px]
          rounded-full
          bg-green-500/10
          -right-52
          -top-44
        " />


        <Link
          to="/"
          className="
            relative
            flex
            items-center
            gap-3
            w-fit
          "
        >

          <div className="
            w-11
            h-11
            rounded-xl
            bg-white
            overflow-hidden
          ">

            <img
              src={logo}
              alt="RentHub"
              className="
                w-full
                h-full
                object-cover
              "
            />

          </div>


          <span className="
            text-xl
            font-bold
          ">
            RentHub
          </span>

        </Link>


        <div className="
          relative
          max-w-xl
        ">

          <div className="
            inline-flex
            items-center
            gap-2
            text-green-300
            bg-green-500/10
            border
            border-green-500/20
            rounded-full
            px-3
            py-2
            text-xs
            font-bold
            mb-7
          ">

            <FiUserCheck />

            GET STARTED WITH RENTHUB

          </div>


          <h1 className="
            text-4xl
            xl:text-5xl
            font-bold
            tracking-tight
            leading-tight
          ">
            Your rent doesn't
            <br />
            have to arrive
            <br />

            <span className="
              text-green-400
            ">
              all at once.
            </span>
          </h1>


          <p className="
            text-gray-400
            text-lg
            leading-8
            mt-6
            max-w-lg
          ">
            Create your account, apply for eligible rent financing and manage repayments from one place.
          </p>


          <div className="
            space-y-4
            mt-10
          ">

            <SignupFeature
              number="01"
              title="Create your account"
              text="Register securely using your email and BVN."
            />

            <SignupFeature
              number="02"
              title="Apply for financing"
              text="Provide your rent, property and landlord details."
            />

            <SignupFeature
              number="03"
              title="Repay monthly"
              text="Track your loan and repayments from your dashboard."
            />

          </div>

        </div>


        <p className="
          relative
          text-xs
          text-gray-500
        ">
          © 2026 RentHub. All rights reserved.
        </p>

      </section>



      {/* SIGNUP FORM */}

      <section className="
        min-h-screen
        flex
        flex-col
      ">


        <div className="
          lg:hidden
          flex
          items-center
          px-5
          py-5
        ">

          <Link
            to="/"
            className="
              flex
              items-center
              gap-2
            "
          >

            <img
              src={logo}
              alt="RentHub"
              className="
                w-10
                h-10
                rounded-xl
                object-cover
              "
            />

            <span className="
              font-bold
            ">
              RentHub
            </span>

          </Link>

        </div>


        <div className="
          flex-1
          flex
          items-center
          justify-center
          px-5
          sm:px-8
          py-10
          lg:py-12
        ">

          <div className="
            w-full
            max-w-[460px]
          ">


            {success ? (

              <SuccessState />

            ) : (

              <>

                <div className="mb-7">

                  <p className="
                    text-sm
                    font-semibold
                    text-green-600
                    mb-2
                  ">
                    Create an account
                  </p>

                  <h2 className="
                    text-3xl
                    lg:text-4xl
                    font-bold
                    tracking-tight
                    text-gray-900
                  ">
                    Get started with RentHub
                  </h2>

                  <p className="
                    text-gray-500
                    mt-3
                    leading-6
                  ">
                    It only takes a few minutes to create your account.
                  </p>

                </div>



                {error && (

                  <div className="
                    bg-red-50
                    border
                    border-red-200
                    rounded-xl
                    px-4
                    py-3
                    text-sm
                    text-red-600
                    mb-6
                  ">
                    {error}
                  </div>

                )}



                <form
                  onSubmit={signUp}
                  className="
                    space-y-5
                  "
                >


                  {/* EMAIL */}

                  <div>

                    <label
                      htmlFor="signup-email"
                      className="
                        block
                        text-sm
                        font-semibold
                        text-gray-700
                        mb-2
                      "
                    >
                      Email address
                    </label>


                    <div className="relative">

                      <FiMail className="
                        absolute
                        left-4
                        top-1/2
                        -translate-y-1/2
                        text-gray-400
                      " />

                      <input
                        id="signup-email"
                        type="email"
                        value={email}
                        onChange={(event) =>
                          setEmail(
                            event.target.value
                          )
                        }
                        placeholder="you@example.com"
                        autoComplete="email"
                        className="
                          w-full
                          h-[54px]
                          pl-11
                          pr-4
                          bg-white
                          border
                          border-gray-300
                          rounded-xl
                          outline-none
                          transition
                          focus:border-green-500
                          focus:ring-4
                          focus:ring-green-50
                        "
                      />

                    </div>

                  </div>



                  {/* BVN */}

                  <div>

                    <label
                      htmlFor="bvn"
                      className="
                        block
                        text-sm
                        font-semibold
                        text-gray-700
                        mb-2
                      "
                    >
                      Bank Verification Number (BVN)
                    </label>


                    <div className="relative">

                      <FiShield className="
                        absolute
                        left-4
                        top-1/2
                        -translate-y-1/2
                        text-gray-400
                      " />


                      <input
                        id="bvn"
                        type="text"
                        inputMode="numeric"
                        maxLength={11}
                        value={bvn}
                        onChange={(event) => {

                          const value =
                            event.target.value
                              .replace(/\D/g, '')

                          setBvn(value)
                        }}
                        placeholder="Enter your 11-digit BVN"
                        className="
                          w-full
                          h-[54px]
                          pl-11
                          pr-4
                          bg-white
                          border
                          border-gray-300
                          rounded-xl
                          outline-none
                          transition
                          focus:border-green-500
                          focus:ring-4
                          focus:ring-green-50
                        "
                      />

                    </div>


                    <div className="
                      flex
                      items-center
                      gap-2
                      mt-2
                    ">

                      <FiShield className="
                        text-green-600
                        shrink-0
                      " />

                      <p className="
                        text-xs
                        text-gray-400
                        leading-5
                      ">
                        Your BVN is used for identity verification and account assessment.
                      </p>

                    </div>

                  </div>



                  {/* PASSWORD */}

                  <div>

                    <label
                      htmlFor="signup-password"
                      className="
                        block
                        text-sm
                        font-semibold
                        text-gray-700
                        mb-2
                      "
                    >
                      Create password
                    </label>


                    <div className="relative">

                      <FiLock className="
                        absolute
                        left-4
                        top-[27px]
                        -translate-y-1/2
                        text-gray-400
                      " />


                      <input
                        id="signup-password"
                        type={
                          showPassword
                            ? 'text'
                            : 'password'
                        }
                        value={password}
                        onChange={(event) =>
                          setPassword(
                            event.target.value
                          )
                        }
                        placeholder="Create a strong password"
                        autoComplete="new-password"
                        className="
                          w-full
                          h-[54px]
                          pl-11
                          pr-12
                          bg-white
                          border
                          border-gray-300
                          rounded-xl
                          outline-none
                          transition
                          focus:border-green-500
                          focus:ring-4
                          focus:ring-green-50
                        "
                      />


                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword(
                            current => !current
                          )
                        }
                        className="
                          absolute
                          right-4
                          top-[27px]
                          -translate-y-1/2
                          text-gray-400
                          hover:text-gray-700
                        "
                      >

                        {
                          showPassword
                            ? <FiEyeOff />
                            : <FiEye />
                        }

                      </button>

                    </div>



                    {password.length > 0 && (

                      <div className="
                        grid
                        grid-cols-1
                        sm:grid-cols-2
                        gap-x-4
                        gap-y-2
                        mt-4
                      ">

                        <Requirement
                          valid={
                            passwordChecks.length
                          }
                          text="8+ characters"
                        />

                        <Requirement
                          valid={
                            passwordChecks.uppercase
                          }
                          text="Uppercase letter"
                        />

                        <Requirement
                          valid={
                            passwordChecks.lowercase
                          }
                          text="Lowercase letter"
                        />

                        <Requirement
                          valid={
                            passwordChecks.number
                          }
                          text="Number"
                        />

                        <Requirement
                          valid={
                            passwordChecks.symbol
                          }
                          text="Symbol"
                        />

                      </div>

                    )}

                  </div>



                  <button
                    type="submit"
                    disabled={
                      loading ||
                      !passwordValid
                    }
                    className="
                      w-full
                      h-[54px]
                      bg-green-600
                      hover:bg-green-700
                      active:bg-green-800
                      disabled:bg-gray-200
                      disabled:text-gray-400
                      disabled:cursor-not-allowed
                      text-white
                      rounded-xl
                      font-bold
                      transition
                      flex
                      items-center
                      justify-center
                      gap-2
                      mt-2
                    "
                  >

                    {
                      loading
                        ? (
                          <>
                            <span className="
                              w-5
                              h-5
                              border-2
                              border-white/40
                              border-t-white
                              rounded-full
                              animate-spin
                            " />

                            Creating account...
                          </>
                        )
                        : (
                          <>
                            Create account

                            <FiArrowRight />
                          </>
                        )
                    }

                  </button>

                </form>



                <p className="
                  text-center
                  text-sm
                  text-gray-500
                  mt-7
                ">
                  Already have an account?{' '}

                  <Link
                    to="/auth/signin/"
                    className="
                      text-green-600
                      hover:text-green-700
                      font-semibold
                    "
                  >
                    Sign in
                  </Link>
                </p>


                <p className="
                  text-xs
                  text-gray-400
                  text-center
                  leading-5
                  mt-5
                ">
                  By creating an account, you agree to RentHub's terms and privacy policy.
                </p>

              </>

            )}

          </div>

        </div>

      </section>

    </div>
  )
}



function Requirement({
  valid,
  text
}) {

  return (

    <div className="
      flex
      items-center
      gap-2
    ">

      <div className={`
        w-4
        h-4
        rounded-full
        flex
        items-center
        justify-center
        text-[10px]

        ${
          valid
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-400'
        }
      `}>

        <FiCheck />

      </div>


      <span className={`
        text-xs

        ${
          valid
            ? 'text-green-700'
            : 'text-gray-400'
        }
      `}>
        {text}
      </span>

    </div>
  )
}



function SignupFeature({
  number,
  title,
  text
}) {

  return (

    <div className="
      flex
      items-start
      gap-4
    ">

      <div className="
        w-9
        h-9
        rounded-xl
        bg-green-500/10
        text-green-400
        flex
        items-center
        justify-center
        text-xs
        font-bold
        shrink-0
      ">
        {number}
      </div>


      <div>

        <p className="
          font-semibold
          text-white
        ">
          {title}
        </p>

        <p className="
          text-sm
          text-gray-400
          mt-1
          leading-5
        ">
          {text}
        </p>

      </div>

    </div>
  )
}



function SuccessState() {

  return (

    <div className="
      bg-white
      border
      border-gray-200
      rounded-3xl
      shadow-sm
      px-7
      py-10
      text-center
    ">

      <div className="
        w-16
        h-16
        bg-green-50
        text-green-600
        rounded-2xl
        flex
        items-center
        justify-center
        text-3xl
        mx-auto
        mb-6
      ">
        <FiCheck />
      </div>


      <h2 className="
        text-2xl
        font-bold
        text-gray-900
      ">
        Check your email
      </h2>


      <p className="
        text-sm
        text-gray-500
        leading-6
        mt-3
      ">
        We've sent you a verification link. Verify your email address to finish setting up your RentHub account.
      </p>


      <p className="
        text-xs
        text-gray-400
        mt-6
      ">
        Redirecting you to sign in...
      </p>

    </div>
  )
}